import { IProductDetail } from '@/api/types/public';
import logger from '@/utils/client-logger';
import { requestPageFullscreen } from '@/utils/fullscreen';
import { Box, Button } from '@mui/material';
import { ARTryOnOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Swiper as SwiperType } from 'swiper';
import MainPicture from './MainPicture';

interface TopCarouselProps {
  productDetail: IProductDetail;
  showTabBtn?: boolean;
  onTryOn?: () => void;
  showTryOn?: boolean;
  isGoodsDetail?: boolean; // 是否是商品详情页
  isWeb?: boolean; // 是否是PC端
  SD_TryONBtn_Exp?: (btn_name: string, item: any, sdEventName: string) => void;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
}

interface TryOnBtnProps {
  isWeb: boolean;
  isGoodsDetail: boolean;
  productDetail: IProductDetail;
  handleTryOn: () => void;
  SD_TryONBtn_Exp?: (btn_name: string, item: any, sdEventName: string) => void;
}

const TryOnBtn = ({
  isWeb,
  isGoodsDetail,
  productDetail,
  handleTryOn,
  SD_TryONBtn_Exp,
}: TryOnBtnProps) => {
  const t = useTranslations('goodsDetail.topCarousel');
  const [tryOnBtnRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });
  const btnName = useMemo(() => {
    if (productDetail.glb_model) {
      return 'AR try on';
    }
    return 'Photo try on';
  }, [productDetail.glb_model]);
  useEffect(() => {
    if (inView) {
      SD_TryONBtn_Exp?.(btnName, productDetail, 'TryONBtn_Exp');
    }
  }, [inView]);
  return (
    <Button
      ref={tryOnBtnRef}
      variant="text"
      sx={{
        position: 'absolute',
        bottom: isWeb && isGoodsDetail ? 'auto' : '3.2vw',
        top: isWeb && isGoodsDetail ? '12px' : 'auto',
        right: isWeb ? '14px' : '3.7333vw',
        cursor: 'pointer',
        minWidth: isWeb ? '90px' : '24vw',
        height: isWeb ? '32px' : '8.5333vw',
        padding: '0 14px',
        fontSize: isWeb ? '14px' : '3.7333vw',
        zIndex: 10,
        backgroundColor: 'rgba(255,255,255,0.8)',
        borderRadius: isWeb ? '16px' : '4.2667vw',
        color: '#000',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.9)',
        },
        '&.not-has-vr': {
          // 如果需要特殊样式
        },
      }}
      className={!productDetail.glb_model ? 'not-has-vr' : ''}
      onClick={() => {
        SD_TryONBtn_Exp?.(btnName, productDetail, 'TryONBtn_Click');
        if (!isWeb) {
          void requestPageFullscreen();
        }
        handleTryOn();
      }}
    >
      {productDetail.glb_model && (
        <>
          <ARTryOnOutlined className="mr-4 text-18" />
          <span>{t('tryOn.ar')}</span>
        </>
      )}
      {!productDetail.glb_model && <span>{t('tryOn.normal')}</span>}
    </Button>
  );
};

const TopCarousel: React.FC<TopCarouselProps> = ({
  productDetail,
  showTabBtn = false,
  onTryOn,
  showTryOn = true,
  isGoodsDetail = false,
  isWeb = false,
  SD_TryONBtn_Exp,
  SDProductDetailsPageBtnClick,
}) => {
  const [tabType, setTabType] = useState('left');
  const MainPictureRef = useRef<{ swiper: SwiperType }>(null);
  const t = useTranslations('goodsDetail.topCarousel');

  const modelName = isGoodsDetail ? t('tabs.onFace') : t('tabs.modeled');
  const tabArr =
    productDetail.imageList?.[0]?.type === 'Modeled'
      ? [
          { name: modelName, value: 'right' },
          { name: t('tabs.product'), value: 'left' },
        ]
      : [
          { name: t('tabs.product'), value: 'left' },
          { name: modelName, value: 'right' },
        ];

  const handleTabChange = (type: string) => {
    setTabType(type);
    const imageList = productDetail.imageList || [];

    // 根据标签类型切换到对应的图片组
    if (type === 'left') {
      // 切换到产品图片组（商品图/素材图）
      const leftIndex = imageList.findIndex((item) => item.type === 'Product');
      if (leftIndex !== -1 && MainPictureRef.current?.swiper) {
        MainPictureRef.current.swiper.slideTo(leftIndex);
      }
    } else {
      // 切换到模特图片组（视频/网红图）
      const rightIndex = imageList.findIndex((item) => item.type === 'Modeled');
      if (rightIndex !== -1 && MainPictureRef.current?.swiper) {
        MainPictureRef.current.swiper.slideTo(rightIndex);
      }
    }
    const btnName = tabArr.find((item) => item.value === type)?.name;
    btnName && SDProductDetailsPageBtnClick?.(btnName);
  };

  const handleTryOn = async () => {
    try {
      onTryOn?.();
    } catch (error) {
      logger.error(error);
    }
  };

  return (
    <Box className={`relative bg-auxiliary-light-gray`}>
      <MainPicture
        productDetail={productDetail}
        images={productDetail.imageList || []}
        onTabTypeChange={setTabType}
        showTabBtn={showTabBtn}
        ref={MainPictureRef}
        isWeb={isWeb}
        isGoodsDetail={isGoodsDetail}
        SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
      />

      {showTabBtn && (
        <Box className="absolute bottom-[15px] left-[14px] box-border w-[175px] h-[24px] rounded-[14px] flex bg-black/30 z-2">
          {tabArr.map((item) => (
            <Button
              key={item.value}
              variant="text"
              size="small"
              sx={{
                minWidth: 'auto',
                borderRadius: '14px',
                padding: 0,
                color: '#fff',
                fontSize: '11px',
                backgroundColor: tabType === item.value ? 'rgba(0,0,0,0.7)' : 'transparent',
              }}
              onClick={() => {
                handleTabChange(item.value);
              }}
              className="w-[66px] h-[24px]"
            >
              {item.name}
            </Button>
          ))}
        </Box>
      )}

      {showTryOn && (productDetail.tryon_image_url || productDetail.glb_model) && (
        <TryOnBtn
          isWeb={isWeb}
          isGoodsDetail={isGoodsDetail}
          productDetail={productDetail}
          handleTryOn={handleTryOn}
          SD_TryONBtn_Exp={SD_TryONBtn_Exp}
        />
      )}
    </Box>
  );
};

export default TopCarousel;
